/**
 * Break a long text blob into readable paragraphs for body copy.
 *
 * Strategy:
 * 1. If the source already contains `\n\n` breaks, respect them (honours
 *    intentional formatting from CMS/Firestore content).
 * 2. Otherwise, split on sentence terminators (`.`, `!`, `?`) followed by
 *    whitespace and an uppercase letter — avoids false splits inside
 *    decimals ("3.5"), initials ("C. Doe"), or abbreviations ("e.g.",
 *    "i.e.") — then chunk into groups of `sentencesPerChunk` sentences.
 *
 * Shorter-than-threshold inputs pass through as a single paragraph.
 */
export function splitIntoParagraphs(
  text: string,
  sentencesPerChunk = 4
): string[] {
  if (!text) return []
  const trimmed = text.trim()
  if (!trimmed) return []

  if (/\n{2,}/.test(trimmed)) {
    return trimmed
      .split(/\n{2,}/)
      .map((s) => s.trim())
      .filter(Boolean)
  }

  // Sentinel-based split avoids regex lookbehind `(?<=...)`, which Safari
  // < 16.4 / older iOS WebKit cannot parse (whole bundle fails to load).
  const SEP = String.fromCharCode(0)
  const sentences = trimmed
    .replace(/([.!?])\s+(?=[A-Z])/g, `$1${SEP}`)
    .split(SEP)
    .map((s) => s.trim())
    .filter(Boolean)

  if (sentences.length <= sentencesPerChunk) return [trimmed]

  const chunks: string[] = []
  for (let i = 0; i < sentences.length; i += sentencesPerChunk) {
    chunks.push(sentences.slice(i, i + sentencesPerChunk).join(" "))
  }
  return chunks
}

/**
 * Split a string into sentence-shaped chunks for visual layout (e.g. one
 * `<span>` per sentence). Uses the same lookbehind-free sentinel split as
 * `splitIntoParagraphs` so the regex parses in Safari < 16.4 / older iOS
 * WebKit.
 */
export function splitIntoSentences(text: string): string[] {
  if (!text) return []
  const SEP = String.fromCharCode(0)
  return text
    .replace(/([.!?])\s+/g, `$1${SEP}`)
    .split(SEP)
    .map((s) => s.trim())
    .filter(Boolean)
}
